import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { ApiUrl } from "../../../ApiUrl";
import { fetchLoggedInTeacherOption } from "../../hooks/fetchLoggedInTeacherOption";

const getContext = () => ({
  academicYearId: localStorage.getItem("academicSessionId"),
  orgId: localStorage.getItem("orgId"),
  branchId: localStorage.getItem("branchId"),
  userId: sessionStorage.getItem("userId"),
});

const today = () => new Date().toISOString().slice(0, 10);

export default function LeaveSheets() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [teacher, setTeacher] = useState(null);
  const [uploadDate, setUploadDate] = useState(today());
  const [remarks, setRemarks] = useState("");
  const [file, setFile] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const loadTeacher = async () => {
    try {
      const loggedInTeacher = await fetchLoggedInTeacherOption();
      setTeacher(loggedInTeacher);
      if (!loggedInTeacher) setMessage("Unable to load the logged-in teacher.");
    } catch {
      setMessage("Unable to load the logged-in teacher.");
    }
  };

  const loadDocuments = async () => {
    const { academicYearId, orgId, branchId, userId } = getContext();
    if (!academicYearId || !orgId || !branchId || !userId) {
      setMessage("Academic session or login details are missing.");
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({ createdBy: userId });
      const response = await fetch(`${ApiUrl.apiurl}ACADEMIC_DOCUMENTS/${academicYearId}/${orgId}/${branchId}/?${params}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Could not load documents.");
      setDocuments(Array.isArray(data.data) ? data.data : []);
      setMessage("");
    } catch (error) {
      setDocuments([]);
      setMessage(error.message || "Could not load documents.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTeacher();
    loadDocuments();
  }, []);

  const clearForm = () => {
    setUploadDate(today());
    setRemarks("");
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setMessage("");
  };

  const uploadDocument = async () => {
    const { academicYearId, orgId, branchId, userId } = getContext();
    if (!file) return setMessage("Select a document before uploading.");
    if (!teacher || !userId || !academicYearId || !orgId || !branchId) {
      return setMessage("Teacher or academic-session details are not ready.");
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("academic_year_id", academicYearId);
      formData.append("org_id", orgId);
      formData.append("branch_id", branchId);
      formData.append("created_by", userId);
      formData.append("remarks", remarks.trim());
      formData.append("upload_file", file);
      const response = await fetch(`${ApiUrl.apiurl}ACADEMIC_DOCUMENTS/create/`, { method: "POST", body: formData });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || data.error || "Upload failed.");
      clearForm();
      await loadDocuments();
      setMessage("Document uploaded successfully.");
    } catch (error) {
      setMessage(typeof error.message === "string" ? error.message : "Upload failed.");
    } finally {
      setLoading(false);
    }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    const { userId } = getContext();
    try {
      const response = await fetch(`${ApiUrl.apiurl}ACADEMIC_DOCUMENTS/deleteACADEMIC_DOCUMENTS/${docId}/?createdBy=${userId}`, { method: "DELETE" });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Delete failed.");
      setDocuments((current) => current.filter((document) => document.doc_id !== docId));
    } catch (error) {
      setMessage(error.message || "Delete failed.");
    }
  };

  const teacherOption = teacher
    ? { value: teacher.value ?? teacher.id, label: teacher.label }
    : null;
  const { academicYearId, orgId, branchId, userId } = getContext();
  const canUpload = Boolean(
    teacher &&
      uploadDate &&
      remarks.trim() &&
      file &&
      academicYearId &&
      orgId &&
      branchId &&
      userId
  );

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card p-0">
            <div className="card-body">
              <p style={{ marginBottom: "0px", textAlign: "center", fontSize: "20px", fontWeight: "700" }}>
                ACADEMIC DOCUMENTS UPLOAD
              </p>
          <div className="row mb-2 mt-3 mx-0">
           <div className="col-12 p-2 d-flex flex-wrap gap-2">
            <button type="button" className="btn btn-primary me-2" style={{ width: "150px" }} onClick={uploadDocument} disabled={loading || !canUpload}>Upload</button>
            <button type="button" className="btn btn-primary me-2" style={{ width: "150px" }} onClick={loadDocuments} disabled={loading}>Display</button>
            <button type="button" className="btn btn-primary me-2" style={{ width: "150px" }} onClick={clearForm} disabled={loading}>Clear</button>
            <button type="button" className="btn btn-danger me-2" style={{ width: "150px" }} onClick={() => navigate("/staff/dashboard")}>Close</button>
          </div>
          </div>
          {message && <div className="alert alert-info py-2">{message}</div>}
          <div className="row mt-3 mx-2">
            <div className="col-12 custom-section-box">
              <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center">
                <div className="row flex-grow-1">
                <div className="col-12 col-md-3 mb-3 mt-3">
                  <label className="form-label fw-bold">Mentor / Teacher *</label>
                  <Select
                    className="details"
                    classNamePrefix="detail-select"
                    value={teacherOption}
                    options={teacherOption ? [teacherOption] : []}
                    placeholder="Loading..."
                    isDisabled
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (base) => ({ ...base, minHeight: "38px" }),
                    }}
                  />
                </div>
                <div className="col-12 col-md-3 mb-0 mt-3">
                  <label className="form-label">Upload Date</label>
                  <input type="date" className="form-control detail" value={uploadDate} onChange={(event) => setUploadDate(event.target.value)} disabled />
                </div>
                <div className="col-12 col-md-3 mb-0 mt-3">
                  <label className="form-label">Remarks</label>
                  <input className="form-control detail" value={remarks} maxLength="2000" placeholder="Enter remarks" onChange={(event) => setRemarks(event.target.value)} required />
                </div>
                <div className="col-12 col-md-3 mb-3 mt-3">
                  <label className="form-label">Select Document *</label>
                  <input ref={fileInputRef} type="file" className="form-control detail" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={(event) => setFile(event.target.files?.[0] || null)} required />
                </div>
              </div>
            </div>
          </div>
          </div>
          <div className="table-responsive">
            <table className="table table-bordered align-middle">
              <thead className="table-primary"><tr><th>Sr.No</th><th>Mentor</th><th>Date</th><th>Remarks</th><th>Attachment</th><th>Delete</th></tr></thead>
              <tbody>
                {loading ? <tr><td colSpan="6" className="text-center">Loading…</td></tr> : documents.length ? documents.map((document, index) => (
                  <tr key={document.doc_id}><td>{index + 1}</td><td>{teacher?.label || "-"}</td><td>{document.upload_date}</td><td>{document.remarks || "-"}</td><td><a href={`${ApiUrl.apiurl}ACADEMIC_DOCUMENTS/download/${document.doc_id}/`}>Download</a></td><td><button className="btn btn-link text-danger p-0" onClick={() => deleteDocument(document.doc_id)}>Delete</button></td></tr>
                )) : <tr><td colSpan="6" className="text-center">No documents found.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      </div>
      </div>
    </div>
  );
}
